const getSchema = rootURL => {
  const gedolimURL = `${rootURL}/s/art/gedolim`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${gedolimURL}`,
      name: 'Gedolim & Rebbe Portraits',
      description:
        'Gedolim and Rebbe portraits available for purchase — prints, paintings, and mixed media. Browse rabbi portraits designed for the wall, framed or unframed.',
      url: gedolimURL,
      isPartOf: { '@id': `${rootURL}/#website` },
      publisher: { '@id': `${rootURL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${gedolimURL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${rootURL}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Art',
          item: `${rootURL}/s/art`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Gedolim & Rebbe Portraits',
          item: gedolimURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${gedolimURL}#faq`,
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Are these rabbi and rebbe portraits paintings, or photographs?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "These are artistic portraits — not photographs. Each piece is painted, illustrated, or created in mixed media using reference imagery. They're designed as decorative wall art for the home.",
          },
        },
        {
          '@type': 'Question',
          name: 'Which Gedolim and Rebbes are available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "The collection includes portraits across a range of Ashkenazi and Sephardic Gedolim, Chassidic Rebbes, and Roshei Yeshiva. Browse the grid to see what's currently available.",
          },
        },
        {
          '@type': 'Question',
          name: 'What formats are available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most portraits are available as prints, original paintings, or mixed media. Sizes vary by piece, and many listings offer both framed and unframed options.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between an original painting and a print?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Original paintings are one-of-a-kind pieces created by the artist. Prints are reproductions of an original work at a lower price point. Mixed media sits in between — often a print with hand-applied elements or layered materials that add depth. Most homes use prints or mixed media for everyday spaces, while originals are usually chosen when someone wants a more central or meaningful piece.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can I commission a portrait of a specific Rebbe or Rav?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes, in many cases. If the Rebbe or Gadol you're looking for isn't available, you can message an artist directly through their listing or profile, or submit a request through Joodaic's commissions hub, where you can choose the style, size, and overall direction of the piece.",
          },
        },
        {
          '@type': 'Question',
          name: 'Are these suitable as gifts?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: "Yes. Gedolim portraits are often chosen for bar mitzvahs, weddings, or yahrzeits. A portrait connected to someone's Rebbe or Rav tends to feel more personal than a general gift.",
          },
        },
      ],
    },
  ];
};

const gedolimSeoConfig = {
  key: 'gedolim',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'gedolim',
  accentColor: '--colorArt',
  links: [],
  title: 'Gedolim & Rebbe Portraits | Prints & Paintings | Joodaic',
  description:
    'Gedolim and Rebbe portraits available for purchase — prints, paintings, and mixed media. Browse rabbi portraits designed for the wall, framed or unframed.',
  getSchema,
  intro: {
    heading: 'Gedolim & Rebbe Portraits',
    tagline:
      'Painted portraits and art prints of the Gedolim and Rebbes you connect to — made for the wall, not the archive.',
    collapsedHeading: 'Gedolim & Rebbe Portraits',
    bodyParagraphs: [],
  },
  bottomSections: [
    {
      id: 'positioning',
      type: 'prose',
      heading: 'Made for the wall',
      paragraphs: [
        "These portraits are not about documentation — they're about presence. Each piece is an artistic interpretation, created as wall art for the home. Some are quiet and traditional. Others bring more texture, depth, or a more modern style. What they share is that they're made to live in a space, not just be looked at.",
        'Many buyers choose these pieces specifically as framed wall portraits, ready to hang in living rooms, dining areas, or study spaces.',
        'You can also explore <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a> if you\'re looking for place-based pieces.',
      ],
    },
    {
      id: 'formats',
      title: 'Prints, paintings, and mixed media',
      paragraphs: [
        'Prints are the most accessible starting point — clean, sharp, and available in multiple sizes. Paintings bring warmth and brushwork that changes how a piece feels on the wall. Mixed media sits in between, combining print with hand-applied elements or layered materials that add depth.',
        'All formats are available framed or unframed.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: [
    {
      question: 'Are these rabbi and rebbe portraits paintings, or photographs?',
      answer: [
        "These are artistic portraits — not photographs. Each piece is painted, illustrated, or created in mixed media using reference imagery. They're designed as decorative wall art for the home.",
      ],
    },
    {
      question: 'Which Gedolim and Rebbes are available?',
      answer: [
        "The collection includes portraits across a range of Ashkenazi and Sephardic Gedolim, Chassidic Rebbes, and Roshei Yeshiva. Browse the grid above to see what's currently available.",
      ],
    },
    {
      question: 'What formats are available?',
      answer: [
        'Most portraits are available as prints, original paintings, or mixed media. Sizes vary by piece, and many listings offer both framed and unframed options.',
      ],
    },
    {
      question: 'What is the difference between an original painting and a print?',
      answer: [
        'Original paintings are one-of-a-kind pieces created by the artist. Prints are reproductions of an original work at a lower price point. Mixed media sits in between — often a print with hand-applied elements or layered materials that add depth.',
        'Most homes use prints or mixed media for everyday spaces, while originals are usually chosen when someone wants a more central or meaningful piece.',
      ],
    },
    {
      question: 'Can I commission a portrait of a specific Rebbe or Rav?',
      answer: [
        "Yes, in many cases. If the Rebbe or Gadol you're looking for isn't available, you can message an artist directly through their listing or profile, or submit a request through the <a href=\"/artist/custom_co_studio\">commissions hub</a> — where you can choose the style, size, and overall direction of the piece.",
      ],
    },
    {
      question: 'Are these suitable as gifts?',
      answer: [
        "Yes. Gedolim portraits are often chosen for bar mitzvahs, weddings, or yahrzeits. A portrait connected to someone's Rebbe or Rav tends to feel more personal than a general gift.",
      ],
    },
  ],
};

export default gedolimSeoConfig;
